import { ReactNode } from 'react'
import { FeatureType, RBACAction, RBACModule } from '@numeo/types-core'
import { UserRole } from 'hooks/useUserRole'

import {
    AlertTriangle,
    AntennaSignal,
    Box,
    ChartLine,
    ChatBubble,
    CircleLetterS,
    ClipboardCheck,
    Dollar,
    Envelope,
    FileCheck,
    FileText,
    Folder,
    GasPump,
    HelpCircle,
    Home,
    LayoutList,
    Magnifier,
    Person,
    PersonGear,
    PersonWorker,
    Persons,
    Receipt,
    Rectangles4,
    Rocket,
    RouteIcon,
    SettingsGear,
    Shield,
    Sliders,
    Sparkles,
    Tray,
    Truck,
    Wallet,
    Wrench,
} from './icons'
import { MARKET_INTEL_MENU_ITEM } from '../../../pages/dashboard/pages/market-intel/menuItem'

/** Gluon-managed operator allowlists that can gate a sidebar row. Add a key here (and to
 *  `SidebarContextFlags.operatorFlags`) when a new gated surface ships. */
export type OperatorFlag = 'dispatchHelp' | 'boxTruck'

/** Flipt flags from /auth/verify (`featureFlags`) that can gate a sidebar row. Add a key here when a new flagged surface ships. */
export type FliptMenuFlag = 'fuelTollWorkspace'

export enum SidebarBadgeTone {
    ACCENT = 'accent',
    SUCCESS = 'success',
    WARNING = 'warning',
    DANGER = 'danger',
}

/**
 * A single leaf menu item (a clickable link).
 */
export interface MenuItem {
    id: string
    label: string
    path: string
    icon: ReactNode
    roles: UserRole[]
    /** Optional feature flag that must be enabled for this item to show */
    feature?: FeatureType
    /** Optional RBAC module that must be viewable for this item to show */
    rbacModule?: RBACModule
    /** Item shows when the user can VIEW ANY ONE of these modules. Use instead of `rbacModule` for a
     *  row reachable through more than one grant — e.g. Pay Adjustments, whose route and API accept
     *  PAY_ADJUSTMENTS or ACCOUNTING, so an existing accounting-only custom role must keep seeing it. */
    rbacModuleAny?: RBACModule[]
    /** Hide unless the account is on the named Gluon-managed operator allowlist (Dispatch Help /
     *  Box Truck Loads). One knob per gated surface — see `SidebarContextFlags.operatorFlags`. */
    requiredOperatorFlag?: OperatorFlag
    /** Hide unless this Flipt flag is on for the account (see `SidebarContextFlags.fliptFlags`). */
    requiredFliptFlag?: FliptMenuFlag
    /** Which half of the Box Truck section this row belongs to. Resolved by `useBoxTruckAccess` against
     *  the REAL account role + RBAC grants (not the feature-derived layout role in `roles` above):
     *  'surfaces' = Loadboard/Bidding/Map, 'management' = the admin-only driver consoles, grantable to
     *  custom roles via the Role builder's Box Truck checkboxes, 'admin' = admin/owner only with no RBAC
     *  escape hatch, 'analytics' = the same plus a Dispatch Manager, who reads only their own desks. */
    boxTruckAccess?: 'surfaces' | 'management' | 'admin' | 'analytics'
    /** Optional sub-section header rendered above this item inside its group's dropdown/popover — items
     *  sharing a `section` value cluster under one small uppercase label. Purely visual: grouping is by
     *  consecutive runs, so keep sectioned items adjacent. */
    section?: string
    /** Optional "NEW" / "BETA" / numeric badge shown inline */
    badge?: { label: string; tone: SidebarBadgeTone }
    /**
     * Soft feature gate: instead of HIDING the row when the feature is off,
     * keep it visible but render it disabled with an inline "Enable" button
     * that opens `enableUrl` (typically a sales / scheduling page) in a new
     * tab. Useful for paid add-ons we want users to discover even when
     * they don't have access yet — e.g. AI Hub for non-AgenticHub accounts.
     */
    paywall?: { feature: FeatureType; enableUrl: string }
}

/**
 * A collapsible group that contains one or more MenuItem children.
 *
 * When `flat` is true the group is rendered as a section header + bare rows
 * even inside the grouped (updater / tmc) layout — the group never collapses.
 * Used for sections that are feature-gated (e.g. Find Loads, visible whenever
 * the user has the Extension feature, regardless of their role).
 */
export interface MenuGroup {
    id: string
    label: string
    icon: ReactNode
    roles: UserRole[]
    /** Group is only rendered if at least one of these features is enabled (empty = always) */
    featureAny?: FeatureType[]
    /** Settings → Team may expose this group even when none of its regular TMS features are enabled. */
    allowBoxTruckProfiles?: boolean
    /**
     * Group is hidden if ANY of these features is enabled. Used for "X-only-when-Y-is-off"
     * fallback rows — e.g. the standalone Fuel AI row only shows when the user does NOT
     * have the Fleet module, because Fleet already nests Fuel AI inside it.
     */
    featureNone?: FeatureType[]
    rbacModule?: RBACModule
    /** Group is shown when the user can VIEW ANY ONE of these modules. Use instead of `rbacModule`
     *  when a group holds rows from different modules — e.g. Accounting, whose Pay Adjustments row is
     *  grantable on its own via PAY_ADJUSTMENTS while the rest stay ACCOUNTING-only. Per-row
     *  `rbacModule` still decides which children actually render. */
    rbacModuleAny?: RBACModule[]
    /** Render as a label + bare items instead of a collapsible dropdown */
    flat?: boolean
    children: MenuItem[]
}

const onboardingGroup: MenuGroup = {
    id: 'onboarding',
    label: 'Onboarding',
    icon: <Rocket />,
    roles: ['dispatcher', 'updater', 'tmc'],
    children: [
        {
            id: 'onboarding',
            label: 'Onboarding',
            path: '/dispatcher',
            icon: <Rocket />,
            roles: ['dispatcher', 'updater', 'tmc'],
        },
    ],
}

const tmsHomeGroup: MenuGroup = {
    id: 'tms-home',
    label: 'Home',
    icon: <Home />,
    roles: ['dispatcher', 'updater', 'tmc'],
    // Only TMS accounts (Fleet/Accounting/Safety) get the TMS Home entry — the page is full of
    // TMS-specific ELD/factoring/fleet content that non-TMS dispatchers shouldn't see.
    featureAny: [FeatureType.Fleet, FeatureType.Accounting, FeatureType.Safety],
    children: [
        {
            id: 'tms-home-link',
            label: 'Home',
            path: '/onboarding/tms/welcome',
            icon: <Home />,
            roles: ['dispatcher', 'updater', 'tmc'],
        },
    ],
}

const dashboardGroup: MenuGroup = {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <Rectangles4 />,
    roles: ['updater', 'tmc'],
    children: [
        {
            id: 'pl-report',
            label: 'P&L Dashboard',
            path: '/accounting/pl-report',
            icon: <ChartLine />,
            roles: ['tmc'],
            feature: FeatureType.Accounting,
            rbacModule: RBACModule.ACCOUNTING,
        },
        {
            id: 'truck-availability',
            label: 'Truck Availability',
            path: '/truck-availability-report',
            icon: <Tray />,
            roles: ['updater', 'tmc'],
            feature: FeatureType.TruckAvailability,
        },
        {
            id: 'task-management',
            label: 'Task Management',
            path: '/updater-agent/task-management',
            icon: <ClipboardCheck />,
            roles: ['updater', 'tmc'],
        },
        {
            id: 'broker-performance',
            label: 'Broker Performance',
            path: '/updater-agent/broker-performance',
            icon: <ChartLine />,
            roles: ['updater', 'tmc'],
            rbacModule: RBACModule.ANALYTICS,
        },
        // Analytics (`/dispatcher/analytics`) was here but it overlaps with
        // Broker Performance / P&L Dashboard. Hidden for now; restore the
        // entry below if we decide it adds standalone value.
        // {
        //     id: 'analytics',
        //     label: 'Analytics',
        //     path: '/dispatcher/analytics',
        //     icon: <ChartLine />,
        //     roles: ['updater', 'tmc'],
        // },
        // Merged in from the standalone Reports group — same routes/RBAC, just
        // reachable from Dashboard's submenu instead of a separate top-level entry.
        {
            id: '1099-reports',
            label: '1099 Reports',
            path: '/accounting/1099-reports',
            icon: <Receipt />,
            roles: ['updater', 'tmc'],
            feature: FeatureType.Accounting,
            rbacModule: RBACModule.ACCOUNTING,
        },
        {
            id: 'truck-pl',
            label: 'Truck P&L',
            path: '/accounting/truck-pl',
            icon: <Truck />,
            roles: ['updater', 'tmc'],
            feature: FeatureType.Accounting,
            rbacModule: RBACModule.ACCOUNTING,
        },
    ],
}

const findLoadsGroup: MenuGroup = {
    id: 'find-loads',
    label: 'Find Loads',
    icon: <Box />,
    roles: ['dispatcher', 'updater', 'tmc'],
    featureAny: [FeatureType.Extension],
    children: [
        {
            id: 'load-hub',
            label: 'Load Hub',
            path: '/dispatcher/load-hub',
            icon: <Magnifier />,
            roles: ['dispatcher', 'updater', 'tmc'],
        },
        {
            id: 'load-radar',
            label: 'Load Radar',
            path: '/dispatcher/load-radar',
            icon: <AntennaSignal />,
            roles: ['dispatcher', 'updater', 'tmc'],
        },
        {
            id: 'ai-hub',
            label: 'AI Hub',
            path: '/dispatcher/ai-hub',
            icon: <Sparkles />,
            roles: ['dispatcher', 'updater', 'tmc'],
        },
        {
            id: 'dispatch-help',
            label: 'Dispatch Help',
            path: '/dispatcher/dispatch-help',
            icon: <HelpCircle />,
            roles: ['dispatcher', 'updater', 'tmc'],
            // Cross-tenant support surface — visible only to Numeo staff on the Gluon-managed allowlist.
            requiredOperatorFlag: 'dispatchHelp',
        },
    ],
}

// Gated "Box Truck Loads" section (Sylectus email loadboard). Every child requires the Box Truck
// Loads operator allowlist, so the whole group disappears for non-operators (filterMenuGroups drops
// a group once all its children are filtered out). Within an operator account the section splits by
// the REAL user role: dispatchers get the working surfaces (Loadboard / Bidding / Map); the driver-
// management surfaces are admin/owner-only. Driver Verifications lives under Profiles with the other
// HR records.
const boxTruckLoadsGroup: MenuGroup = {
    id: 'box-truck-loads',
    label: 'Box Truck',
    icon: <Truck />,
    roles: ['dispatcher', 'updater', 'tmc'],
    // Dispatcher working surfaces. HR records live under Profiles.
    children: [
        {
            id: 'box-truck-loadboard',
            label: 'Loadboard',
            path: '/dispatcher/box-truck-loadboard',
            icon: <Truck />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'surfaces',
            section: 'Dispatchers',
        },
        {
            id: 'box-truck-bidding',
            label: 'Bidding',
            path: '/dispatcher/box-truck-bidding',
            icon: <Truck />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'surfaces',
            section: 'Dispatchers',
        },
        {
            id: 'box-truck-my-drivers',
            label: 'My Drivers',
            path: '/dispatcher/box-truck-my-drivers',
            icon: <Persons />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'management',
            section: 'Dispatchers',
        },
        {
            id: 'box-truck-map',
            label: 'Map',
            path: '/dispatcher/box-truck-map',
            icon: <AntennaSignal />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'surfaces',
            section: 'Dispatchers',
        },
        {
            id: 'box-truck-mailing',
            label: 'Mailing',
            path: '/dispatcher/box-truck-mailing',
            icon: <Envelope />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'management',
            section: 'Dispatchers',
        },
        {
            id: 'box-truck-sms',
            label: 'SMS',
            path: '/dispatcher/box-truck-sms',
            icon: <ChatBubble />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'management',
            section: 'Dispatchers',
        },
        {
            id: 'box-truck-analytics',
            label: 'Analytics',
            path: '/dispatcher/box-truck-analytics',
            icon: <ChartLine />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            // Not a 'management' row: the page measures the dispatchers. Admin/owner see the tenant; a
            // Dispatch Manager sees the desks they manage, narrowed server-side.
            boxTruckAccess: 'analytics',
            section: 'Dispatchers',
        },
    ],
}

// ── 3. Manage Loads (updater / tmc) ──────────────────────────────────────────
// Reference: Dispatch Board, Ongoing Loads, Fleet View (Fuel AI moved to Fleet)
const manageLoadsGroup: MenuGroup = {
    id: 'manage-loads',
    label: 'Manage Loads',
    icon: <Sliders />,
    roles: ['updater', 'tmc'],
    featureAny: [FeatureType.Updater, FeatureType.UpdaterLite],
    children: [
        {
            id: 'dispatch-board',
            label: 'Dispatch Board',
            path: '/updater-agent/scheduler',
            icon: <LayoutList />,
            roles: ['updater', 'tmc'],
        },
        {
            id: 'ongoing-loads',
            label: 'Ongoing Loads',
            path: '/updater-agent/loads',
            icon: <RouteIcon />,
            roles: ['updater', 'tmc'],
            // VIEW only gates the ROW. Whether the loads on it can be added, edited or deleted is the same
            // module's other actions, asked per control by `useOngoingLoadsAccess`. A role that predates
            // the module keeps both (legacy grants, RBAC_PERMISSIONS_VERSION 2).
            rbacModule: RBACModule.ONGOING_LOADS,
        },
        {
            id: 'fleet-view',
            label: 'Fleet View',
            path: '/updater-agent',
            icon: <Truck />,
            roles: ['updater', 'tmc'],
        },
    ],
}

// ── 4. Accounting (tmc) ──────────────────────────────────────────────────────
// Reference: Invoices (AR), Driver Pay, Contractor Pay, Payroll, Pay Adjustments (merged Expenses + Prepayments)
// Payroll is omitted (no admin route). Factoring is an admin-only extra appended
// at the end. Reports and P&L Report used to live here too — they've moved out:
// P&L Report to the Dashboard group and Reports to its own standalone group
// next to Profiles (visible to any non-EXTENSION-only user).
const accountingGroup: MenuGroup = {
    id: 'accounting',
    label: 'Accounting',
    icon: <CircleLetterS />,
    roles: ['tmc'],
    featureAny: [FeatureType.Accounting],
    // Either module opens the section; the children decide what's inside it. A role granted only
    // PAY_ADJUSTMENTS gets an Accounting section containing just Pay Adjustments, because every other
    // child below requires ACCOUNTING. `filterMenuGroups` drops a group whose children all filter out,
    // so a user with neither module still sees nothing.
    rbacModuleAny: [RBACModule.ACCOUNTING, RBACModule.PAY_ADJUSTMENTS],
    children: [
        {
            id: 'invoices',
            label: 'Invoices (AR)',
            path: '/accounting/invoices',
            icon: <Receipt />,
            roles: ['tmc'],
            // Explicit now that the group accepts PAY_ADJUSTMENTS too — without this, a
            // Pay-Adjustments-only role would see this row.
            rbacModule: RBACModule.ACCOUNTING,
        },
        {
            id: 'driver-pay',
            label: 'Driver Pay',
            path: '/accounting/driver-pay',
            icon: <Person />,
            roles: ['tmc'],
            // Explicit now that the group accepts PAY_ADJUSTMENTS too — without this, a
            // Pay-Adjustments-only role would see this row.
            rbacModule: RBACModule.ACCOUNTING,
        },
        {
            id: 'contractor-pay',
            label: 'Contractor Pay',
            path: '/accounting/contractor-pay',
            icon: <PersonWorker />,
            roles: ['tmc'],
            // Explicit now that the group accepts PAY_ADJUSTMENTS too — without this, a
            // Pay-Adjustments-only role would see this row.
            rbacModule: RBACModule.ACCOUNTING,
        },
        {
            id: 'fuel-toll',
            label: 'Fuel & Toll',
            path: '/accounting/fuel-toll',
            icon: <GasPump />,
            roles: ['tmc'],
            rbacModuleAny: [RBACModule.PAY_ADJUSTMENTS, RBACModule.ACCOUNTING],
            // Off, fuel and toll stay on Pay Adjustments' own tabs.
            requiredFliptFlag: 'fuelTollWorkspace',
        },
        {
            id: 'expenses-adjustments',
            label: 'Expenses & Adjustments',
            path: '/accounting/expenses-adjustments',
            icon: <FileText />,
            roles: ['tmc'],
            // This is a separate experience from Pay Adjustments, but it reads and mutates the
            // same accounting-owned records and therefore follows the same action grants.
            rbacModuleAny: [RBACModule.PAY_ADJUSTMENTS, RBACModule.ACCOUNTING],
            // Ships with the Fuel & Toll workspace; off, the company keeps Pay Adjustments.
            requiredFliptFlag: 'fuelTollWorkspace',
        },
        {
            // Merged section: replaces the old Expenses and Prepayments items (their routes
            // redirect here, landing on the matching tab).
            id: 'pay-adjustments',
            label: 'Pay Adjustments',
            path: '/accounting/pay-adjustments',
            icon: <Dollar />,
            roles: ['tmc'],
            // Either module shows this row, mirroring the route guard and the backend endpoints.
            // PAY_ADJUSTMENTS lets an admin grant the page on its own from Settings → Roles; keeping
            // ACCOUNTING here means an existing accounting-only CUSTOM role (an explicit allowlist that
            // won't contain the new module) doesn't silently lose the row it has today.
            rbacModuleAny: [RBACModule.PAY_ADJUSTMENTS, RBACModule.ACCOUNTING],
        },
        // Admin-only extras below:
        {
            id: 'factoring',
            label: 'Factoring',
            path: '/accounting/factoring',
            icon: <Wallet />,
            roles: ['tmc'],
            // Explicit now that the group accepts PAY_ADJUSTMENTS too — without this, a
            // Pay-Adjustments-only role would see this row.
            rbacModule: RBACModule.ACCOUNTING,
        },
    ],
}

// ── Instant Pay (tmc) — its own top-level section, NOT nested under Accounting ─
// Single-child group, so Sidebar renders it as one bare top-level row (no dropdown
// shell). Gated on the Instant Pay company feature (default off) — it does NOT require
// the Accounting FEATURE, so a carrier can run Instant Pay standalone — but it DOES
// require the ACCOUNTING RBAC permission (like accountingGroup), because it moves real
// cash: a role denied Accounting must not be able to approve advances or top up wallets.
const instantPayGroup: MenuGroup = {
    id: 'instant-pay-group',
    label: 'Instant Pay',
    icon: <Wallet />,
    roles: ['tmc'],
    featureAny: [FeatureType.InstantPay],
    rbacModule: RBACModule.ACCOUNTING,
    children: [
        {
            id: 'instant-pay',
            label: 'Instant Pay',
            path: '/instant-pay',
            icon: <Wallet />,
            roles: ['tmc'],
            feature: FeatureType.InstantPay,
            rbacModule: RBACModule.ACCOUNTING,
        },
    ],
}

// ── 5. Fleet Management (tmc) ────────────────────────────────────────────────
// Reference: Fleet Overview, Compliance, Maintenance, Fuel AI
// Inspections is admin-only and appended at the end.
const fleetGroup: MenuGroup = {
    id: 'fleet',
    label: 'Fleet Management',
    icon: <Truck />,
    roles: ['tmc'],
    featureAny: [FeatureType.Fleet],
    rbacModule: RBACModule.FLEET,
    children: [
        {
            id: 'fleet-overview',
            label: 'Fleet Overview',
            path: '/fleet/overview',
            icon: <Rectangles4 />,
            roles: ['tmc'],
        },
        {
            id: 'fleet-compliance',
            label: 'Compliance',
            path: '/fleet/compliance',
            icon: <FileCheck />,
            roles: ['tmc'],
        },
        {
            id: 'fleet-maintenance',
            label: 'Maintenance',
            path: '/fleet/maintenance',
            icon: <Wrench />,
            roles: ['tmc'],
        },
        {
            id: 'fuel-ai',
            label: 'Fuel AI',
            path: '/fuel-ai',
            icon: <GasPump />,
            roles: ['tmc'],
            feature: FeatureType.FuelAI,
            badge: { label: 'NEW', tone: SidebarBadgeTone.SUCCESS },
        },
        // Admin-only extras below:
        {
            id: 'fleet-inspections',
            label: 'Inspections',
            path: '/fleet/inspections',
            icon: <ClipboardCheck />,
            roles: ['tmc'],
        },
    ],
}

// ── 6. Safety (tmc) ──────────────────────────────────────────────────────────
// Reference: Dashboard, Compliance, DOT Inspections, Accidents & Claims, Drug & Alcohol, IFTA
// Admin uses "Driver Compliance" (not "Compliance") because the route is /safety/driver-compliance.
// Company Files is admin-only and appended at the end.
const safetyGroup: MenuGroup = {
    id: 'safety',
    label: 'Safety',
    icon: <Shield />,
    roles: ['tmc'],
    featureAny: [FeatureType.Safety],
    rbacModule: RBACModule.SAFETY,
    children: [
        {
            id: 'safety-dashboard',
            label: 'Dashboard',
            path: '/safety/dashboard',
            icon: <Rectangles4 />,
            roles: ['tmc'],
        },
        {
            id: 'safety-driver-compliance',
            label: 'Driver Compliance',
            path: '/safety/driver-compliance',
            icon: <FileCheck />,
            roles: ['tmc'],
        },
        {
            id: 'safety-dot-inspections',
            label: 'DOT Inspections',
            path: '/safety/dot-inspections',
            icon: <ClipboardCheck />,
            roles: ['tmc'],
        },
        {
            id: 'safety-accidents',
            label: 'Accidents & Claims',
            path: '/safety/accidents',
            icon: <AlertTriangle />,
            roles: ['tmc'],
        },
        {
            id: 'safety-drug-alcohol',
            label: 'Drug & Alcohol',
            path: '/safety/drug-alcohol',
            icon: <Shield />,
            roles: ['tmc'],
        },
        {
            id: 'safety-ifta',
            label: 'IFTA',
            path: '/safety/ifta',
            icon: <FileText />,
            roles: ['tmc'],
        },
        // Admin-only extras below:
        {
            id: 'safety-company-files',
            label: 'Company Files',
            path: '/safety/company-files',
            icon: <Folder />,
            roles: ['tmc'],
        },
    ],
}

// ── 7. Profiles (Updater / TMC / FuelAI customers) ───────────────────────────
// Existing behavior: updater role (Updater / UpdaterLite) and tmc role
// (Fleet / Accounting / Safety) saw Profiles automatically via role gating.
// New: FuelAI alone also unlocks Profiles because driver/asset records feed
// the fuel-optimization workflow — so a dispatcher with only FuelAI enabled
// (role resolves to `dispatcher`) still needs the section.
//
// Pure Extension-only dispatchers match no feature here, so they keep their
// original Profiles-free sidebar. The routes check the same Organization
// modules (pages/profiles/routes.tsx), so hiding a row also closes its URL.
const profilesGroup: MenuGroup = {
    id: 'profiles',
    label: 'Profiles',
    icon: <Persons />,
    roles: ['dispatcher', 'updater', 'tmc'],
    featureAny: [FeatureType.Updater, FeatureType.UpdaterLite, FeatureType.Fleet, FeatureType.Accounting, FeatureType.Safety, FeatureType.FuelAI],
    allowBoxTruckProfiles: true,
    // No group-level Organization check: each Organization row's module already needs Organization (see
    // MODULE_METADATA[*].parent), and Driver Verifications is the operator's recruitment queue, kept by its own
    // box-truck tier — a recruiter role with only "Box Truck Driver Management" ticked must still reach it.
    children: [
        {
            id: 'profiles-drivers',
            label: 'Drivers',
            path: '/profiles/drivers',
            icon: <Person />,
            roles: ['dispatcher', 'updater', 'tmc'],
            rbacModule: RBACModule.DRIVER_PROFILES,
        },
        {
            id: 'box-truck-driver-verifications',
            label: 'Driver Verifications',
            path: '/dispatcher/box-truck-driver-verifications',
            icon: <Truck />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'management',
        },
        {
            // The SAME page as the Box Truck group's SMS row, reachable from here too, and a separate id
            // so a role can hold one without the other. An HR recruiter's menu is the Profiles rows and
            // nothing else — they never see the Box Truck group — yet texting a driver is most of the
            // job, and the endpoints behind it already admit them (management + operator, which is what
            // Driver Verifications beside it needs). Without this row the page existed and they could
            // not open it.
            id: 'box-truck-driver-sms',
            label: 'SMS',
            path: '/dispatcher/box-truck-sms',
            icon: <ChatBubble />,
            roles: ['dispatcher', 'updater', 'tmc'],
            requiredOperatorFlag: 'boxTruck',
            boxTruckAccess: 'management',
        },
        {
            id: 'profiles-contractors',
            label: 'Contractors',
            path: '/profiles/contractors',
            icon: <PersonWorker />,
            roles: ['dispatcher', 'updater', 'tmc'],
            rbacModule: RBACModule.CONTRACTOR_PROFILES,
        },
        {
            id: 'profiles-assets',
            label: 'Assets',
            path: '/profiles/assets',
            icon: <Tray />,
            roles: ['dispatcher', 'updater', 'tmc'],
            rbacModule: RBACModule.ASSET_PROFILES,
        },
        {
            id: 'profiles-brokers',
            label: 'Brokers',
            path: '/profiles/brokers',
            icon: <PersonGear />,
            roles: ['dispatcher', 'updater', 'tmc'],
            rbacModule: RBACModule.BROKER_PROFILES,
        },
    ],
}

const fuelAiGroup: MenuGroup = {
    id: 'fuel-ai',
    label: 'Fuel AI',
    icon: <GasPump />,
    roles: ['dispatcher', 'updater', 'tmc'],
    featureAny: [FeatureType.FuelAI],
    featureNone: [FeatureType.Fleet],
    children: [
        {
            id: 'fuel-ai',
            label: 'Fuel AI',
            path: '/fuel-ai',
            icon: <GasPump />,
            roles: ['dispatcher', 'updater', 'tmc'],
            feature: FeatureType.FuelAI,
            badge: { label: 'NEW', tone: SidebarBadgeTone.SUCCESS },
        },
    ],
}

// Order matters here beyond simple priority: groups belonging to the same sidebar
// section (Numeo Spot / Numeo One TMS / Organization — see SECTION_FOR_GROUP in
// Sidebar.tsx) must sit contiguously so their shared section header renders once,
// above the first group in that run, instead of repeating every time the section
// reappears.
const MENU_GROUPS: MenuGroup[] = [
    onboardingGroup,
    tmsHomeGroup,
    // ── Numeo Spot ───────────────────────────────────────────────────────────
    findLoadsGroup,
    boxTruckLoadsGroup,
    // ── Market Intel (all roles, gated by Flipt flag) ───────────────────────
    {
        id: 'market-intel-group',
        label: 'Market Intel',
        icon: <ChartLine />,
        roles: ['dispatcher', 'updater', 'tmc'] as UserRole[],
        children: [MARKET_INTEL_MENU_ITEM],
    } satisfies MenuGroup,
    // ── Numeo One TMS ────────────────────────────────────────────────────────
    dashboardGroup,
    manageLoadsGroup,
    accountingGroup,
    instantPayGroup,
    fleetGroup,
    safetyGroup,
    fuelAiGroup,
    // ── Organization ─────────────────────────────────────────────────────────
    profilesGroup,
]

interface StandaloneItem extends MenuItem {
    /** This row survives a role's menu selection. Enforced by `menuVisibilityFilter`, and the same flag
     *  keeps the row out of the Role builder's picker — an unhideable row must not be offered as a
     *  checkbox that does nothing. Set it only where hiding the row would be a trap. */
    alwaysVisible?: boolean
}

export const BILLING_ITEM: StandaloneItem = {
    id: 'billing',
    label: 'Billing',
    path: '/billing',
    icon: <Wallet />,
    roles: ['dispatcher', 'updater', 'tmc'],
}

export const HELP_CENTER_ITEM: StandaloneItem = {
    id: 'help-center',
    label: 'Help Center',
    path: '/help-center',
    icon: <HelpCircle />,
    roles: ['dispatcher', 'updater', 'tmc'],
    alwaysVisible: true,
}

// NOT `alwaysVisible`. It carried the flag while the flag did nothing, which read as a guarantee the code
// never made. Settings is genuinely hideable: only the OWNER can set a menu selection (every mutating RBAC
// route is behind `ownerOnlyMiddleware`, and the Roles tab only renders for the owner), and the owner is
// always exempt from one — so nobody who can lift a selection can be subject to it.
export const SETTINGS_ITEM: StandaloneItem = {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: <SettingsGear />,
    roles: ['dispatcher', 'updater', 'tmc'],
}

/** Rows no menu selection may take away. Derived from the flag so the two are one fact, not two. */
const ALWAYS_VISIBLE_MENU_IDS = new Set([BILLING_ITEM, HELP_CENTER_ITEM, SETTINGS_ITEM].filter((item) => item.alwaysVisible).map((item) => item.id))

interface SidebarContextFlags {
    hasFeature: (feature: FeatureType) => boolean
    hasRbac: (module: RBACModule, action: RBACAction) => boolean
    isTmsAuthUser?: boolean
    /** Which Gluon-managed operator allowlists this account is on. A row with `requiredOperatorFlag: F`
     *  is shown only when `operatorFlags[F]` is true. */
    operatorFlags?: Partial<Record<OperatorFlag, boolean>>
    /** The Flipt flags /auth/verify reported. A row with `requiredFliptFlag: F` is shown only when `fliptFlags[F]` is true. */
    fliptFlags?: Partial<Record<FliptMenuFlag, boolean>>
    /** Resolved box-truck access tiers (see `useBoxTruckAccess`) — gates `boxTruckAccess` rows.
     *  Defaults to hidden when absent, so a missing wire-up can't leak the admin consoles. */
    boxTruckAccess?: { surfaces: boolean; management: boolean; admin: boolean; analytics: boolean }
    /** Per-company-member Settings value; affects Profiles navigation only. */
    boxTruckProfilesMode?: boolean
    /** The caller's role restricts the sidebar to these menu item ids. Absent or empty = no restriction,
     *  which is every role that predates the field. Applied LAST, so it can only ever take rows away —
     *  it cannot reveal one that a feature, an operator allowlist or an RBAC grant has already denied. */
    visibleMenuIds?: string[]
}

/**
 * A role's menu selection, as a predicate.
 *
 * One definition, two callers: the grouped nav below, and the standalone Billing / Settings rows the
 * Sidebar renders by hand outside `MENU_GROUPS`. Those two bypass `filterMenuGroups` entirely, so without
 * this they would survive every selection — "show only the Loadboard" would still show Billing.
 *
 * No selection means everything passes, which is what makes the field opt-in. An `alwaysVisible` row
 * passes whatever the selection says — that flag used to be read nowhere at all, so Help Center survived
 * only because the Sidebar happened not to filter it. Enforcing it here makes the guarantee real, and
 * means the next row that sets the flag gets it without anyone remembering a call site.
 */
export const menuVisibilityFilter = (visibleMenuIds?: string[]): ((menuId: string) => boolean) => {
    const allowed = visibleMenuIds?.length ? new Set(visibleMenuIds) : null
    return (menuId: string) => !allowed || allowed.has(menuId) || ALWAYS_VISIBLE_MENU_IDS.has(menuId)
}

/** One selectable sidebar row, for the Role builder's menu picker. */
interface MenuCatalogueGroup {
    id: string
    label: string
    items: { id: string; label: string }[]
}

/**
 * The sidebar flattened to ids and labels, so the Role builder can offer it as checkboxes.
 *
 * Derived here rather than listed again elsewhere: `MENU_GROUPS` stays private (the only way to render
 * the menu is still `filterMenuGroups`, which applies every gate), and a hand-kept second copy would
 * drift — quietly failing to offer rows that exist while still offering rows that don't.
 *
 * Ids are unique across the whole catalogue, first occurrence wins. A row may appear in two groups that
 * never render together — Fuel AI sits in Fleet and in its own group, which is gated `featureNone: Fleet`
 * — and the sidebar is fine with that because only one of the two survives the gates. The picker is not:
 * it draws every group ungated, so the same id would become two checkboxes sharing one piece of state,
 * both ticking at once while the count underneath insisted one row was selected.
 */
export const menuCatalogue = (): MenuCatalogueGroup[] => {
    const claimed = new Set<string>()
    const fromGroups: MenuCatalogueGroup[] = []
    for (const group of MENU_GROUPS) {
        const items = []
        for (const child of group.children) {
            if (claimed.has(child.id)) continue
            claimed.add(child.id)
            items.push({ id: child.id, label: child.label })
        }
        if (items.length > 0) fromGroups.push({ id: group.id, label: group.label, items })
    }

    // Billing and Settings live outside MENU_GROUPS — the Sidebar renders them by hand under
    // "Organization" — but a selection that could not reach them would be a lie: "only the Loadboard"
    // has to mean only the Loadboard. Help Center is filtered out by `alwaysVisible`: offering a
    // checkbox for a row the filter will pass anyway is a control that does nothing.
    const organization = [BILLING_ITEM, HELP_CENTER_ITEM, SETTINGS_ITEM]
        .filter((item) => !ALWAYS_VISIBLE_MENU_IDS.has(item.id))
        .map((item) => ({ id: item.id, label: item.label }))

    return [...fromGroups, { id: 'organization', label: 'Organization', items: organization }]
}

/**
 * Every row a selection can name, flat.
 *
 * Beside the catalogue because it is a property OF the catalogue: the picker starts a selection from it,
 * and `presetMenuIds` uses it to drop rows a preset names that the menu no longer has. Two copies of "what
 * ids exist" would disagree the first time a row is renamed in one of them.
 */
export const allMenuIds = (): string[] => menuCatalogue().flatMap((group) => group.items.map((item) => item.id))

/**
 * The sidebar a role actually gets: its groups, and the predicate for the rows the Sidebar draws by hand.
 *
 * One entry point for both, because they have to agree — and because of the rule that needs them
 * together: **a selection that would leave the rail completely empty is ignored.**
 *
 * The selection is subtractive, so naming rows the account cannot show subtracts everything and leaves
 * nothing behind. That is easy to do by accident — the Box Truck presets name rows gated by the Box Truck
 * operator allowlist, so applying one on an account that is not an operator empties the sidebar. And an
 * empty sidebar is not a stricter sidebar: it is a user with no way to navigate, who cannot even reach
 * Settings to ask for it to be undone. Falling back to the unrestricted rail shows more than the owner
 * asked for, which is a presentation mistake; stranding someone is not.
 */
export const resolveSidebarMenu = (role: UserRole, flags: SidebarContextFlags): { groups: MenuGroup[]; allowsMenuId: (menuId: string) => boolean } => {
    const groups = filterMenuGroups(role, flags)
    const allowsMenuId = menuVisibilityFilter(flags.visibleMenuIds)

    const leavesSomething = groups.length > 0 || allowsMenuId(BILLING_ITEM.id) || allowsMenuId(SETTINGS_ITEM.id)
    if (!flags.visibleMenuIds?.length || leavesSomething) return { groups, allowsMenuId }

    console.warn('[sidebar] menu selection matched no visible row; showing the unrestricted sidebar', flags.visibleMenuIds)
    return { groups: filterMenuGroups(role, { ...flags, visibleMenuIds: undefined }), allowsMenuId: menuVisibilityFilter(undefined) }
}

export const filterMenuGroups = (role: UserRole, flags: SidebarContextFlags): MenuGroup[] => {
    const allowsMenuId = menuVisibilityFilter(flags.visibleMenuIds)
    return MENU_GROUPS.map((group) => {
        if (!group.roles.includes(role)) return null
        if (group.featureAny && !group.featureAny.some(flags.hasFeature) && !(group.allowBoxTruckProfiles && flags.boxTruckProfilesMode)) {
            // tms-home is also visible for TMS-authenticated users who haven't connected features yet
            if (!(group.id === 'tms-home' && flags.isTmsAuthUser)) return null
        }
        if (group.featureNone && group.featureNone.some(flags.hasFeature)) return null
        if (group.rbacModule && !flags.hasRbac(group.rbacModule, RBACAction.VIEW)) return null
        if (group.rbacModuleAny && !group.rbacModuleAny.some((module) => flags.hasRbac(module, RBACAction.VIEW))) return null

        const children = group.children
            .filter((child) => {
                if (!child.roles.includes(role)) return false
                if (child.feature && !flags.hasFeature(child.feature)) return false
                if (child.requiredOperatorFlag && !flags.operatorFlags?.[child.requiredOperatorFlag]) return false
                if (child.requiredFliptFlag && !flags.fliptFlags?.[child.requiredFliptFlag]) return false
                if (child.boxTruckAccess && !flags.boxTruckAccess?.[child.boxTruckAccess]) return false
                // NOTE: TMS module items are intentionally NOT filtered/locked in the sidebar. They stay
                // visible + clickable; a missing entitlement surfaces as the full-page FeatureGate takeover
                // on navigation — see ADR-020 (gating UX = full takeover on click, not hide/grey).
                if (child.rbacModule && !flags.hasRbac(child.rbacModule, RBACAction.VIEW)) return false
                if (child.rbacModuleAny && !child.rbacModuleAny.some((module) => flags.hasRbac(module, RBACAction.VIEW))) return false
                // LAST, and subtractive by construction: every check above has already had its say, so a role's
                // menu selection can narrow what it sees and never widen it. A group whose children all fall
                // away here disappears with them, through the existing empty-group rule below.
                if (!allowsMenuId(child.id)) return false
                return true
            })
            .map((child) => (flags.boxTruckProfilesMode && child.id === 'profiles-contractors' ? { ...child, label: 'Owners' } : child))

        if (children.length === 0) return null
        return {
            ...group,
            children: flags.boxTruckProfilesMode
                ? children.map((child) => (child.id === 'profiles-assets' ? { ...child, label: 'Vehicles' } : child))
                : children,
        }
    }).filter((group): group is MenuGroup => group !== null)
}
